<template>
  <div class="home">
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-4 mb-lg-0">
              <div class="d-flex align-items-center">
                <div>
                  <h4 class="mb-0 fw-bold text-dark">{{$t('reservations')}}</h4>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="keyword" :placeholder="$t('search')">
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="px-8">
        <div class="row align-items-end g-3 mb-4">
          <div class="col-md-3 col-lg-2" v-if="currentView == 'table'">
            <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" :shortcuts="shortcuts" range :placeholder="$t('select_date_range')"></date-picker>
          </div>
          <div class="col"></div>
          <div class="col-md-auto">
            <button @click="toggleView" class="btn btn-white border shadow-sm px-4 rounded-3 fw-bold transition-all" style="padding: 5px 10px !important">
              <AppIcon :name="currentView === 'table' ? 'bi bi-calendar-event' : 'bi bi-table'" :class="currentView === 'table' ? 'bi bi-calendar-event' : 'bi bi-table'"  />
            </button>
          </div>
          <template v-if="helper.checkAccess('create_reservations') != -1">
            <div class="col-md-auto">
              <button @click="create" class="btn btn-success px-4 rounded-3 fw-bold shadow-sm transition-all">
                <span class="d-inline-block me-2">
                  <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
                </span>
                <span>{{$t('new_reservation') || $t('reservation')}}</span>
              </button>
            </div>
          </template>
        </div>

        <!-- Table View -->
        <div v-if="currentView === 'table'" class="page-table-container mb-6 bg-white rounded shadow">
          <!-- table -->
          <div class="table-responsive">
            <table class="table mb-0 table-borderless table-striped small">
              <thead>
                <tr class="text-secondary">
                  <th class="pt-4 pb-3 px-6" style="width: 40px;">
                    <div class="form-check mb-0">
                      <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                    </div>
                  </th>
                  <sortable-table-header field="id" label="id" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchReservations)" />
                  <sortable-table-header field="date" label="date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchReservations)" />
                  <sortable-table-header field="time" label="time" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchReservations)" />
                  <th class="pt-4 pb-3 px-6">{{$t('client')}}</th>
                  <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchReservations)" />
                  <sortable-table-header field="notes" label="notes" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchReservations)" />
                  <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in reservations" :key="reservation.id" :class="{'table-active': reservation.selected}">
                  <td class="py-5 px-6">
                    <div class="form-check mb-0">
                      <input class="form-check-input" type="checkbox" v-model="reservation.selected" @change="changeSelectedItem">
                    </div>
                  </td>
                  <td class="py-5 px-6 fw-bold text-dark">#{{ String(reservation.id).substring(0, 8) }}</td>
                  <td class="py-5 px-6">{{ formatDate(reservation.date) }}</td>
                  <td class="py-5 px-6">
                    <span class="bg-light px-2 py-1 rounded-2 text-dark small fw-bold border">
                      {{ reservation.time }}
                    </span>
                  </td>
                  <td class="py-5 px-6">
                    <div class="d-flex align-items-center">
                      <div class="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-2 fw-bold" style="width: 28px; height: 28px; font-size: 11px;">
                        {{ reservation?.client?.name ? reservation.client.name.charAt(0).toUpperCase() : '?' }}
                      </div>
                      <span class="fw-bold">{{ reservation?.client?.name || '-' }}</span>
                    </div>
                  </td>
                  <td class="py-5 px-6">
                    <span :class="statusBadgeClass(reservation.status)" class="badge rounded-pill small px-3">
                      {{ $t(reservation.status) }}
                    </span>
                  </td>
                  <td class="py-5 px-6 text-muted small text-truncate" style="max-width: 150px;">{{ reservation.notes || '-' }}</td>
                  <td class="py-5 px-6">
                    <div class="d-flex gap-2">
                      <button v-if="helper.checkAccess('view_reservations') != -1" @click="viewReservation(reservation)" class="btn btn-outline-primary btn-xs px-2">
                        <AppIcon name="bi-eye" class="bi bi-eye" /> {{$t('view')}}
                      </button>
                      <button v-if="helper.checkAccess('edit_reservations') != -1" @click="editReservation(reservation)" class="btn btn-success btn-xs px-2 shadow-sm">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" /> {{$t('edit')}}
                      </button>
                      <button v-if="helper.checkAccess('delete_reservations') != -1" @click="deleteReservation(reservation)" class="btn btn-danger btn-xs px-2 shadow-sm">
                        <AppIcon name="bi-trash" class="bi bi-trash" /> {{$t('delete')}}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="count > reservations.length" class="d-flex justify-content-center mt-4 mb-2">
              <compact-pagination :pages="pagination" :current-page="page" @change="fetchReservations" />
            </div>
          </div>
          <!-- table -->
          <table-loading-bar v-if="loading" class="page-table-state text-center py-5" />
          <div v-if="loading === false && !reservations.length" class="page-table-state text-center py-5 border rounded-4 bg-light" style="border-style: dashed !important;">
             <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-muted mb-3 d-block" style="font-size: 3rem;" />
             <h5 class="text-dark fw-bold">{{$t('no_reservations_found')}}</h5>
             <p class="text-muted small">{{$t('try_adjusting_filters') || 'Try adjusting your search or filters' }}</p>
          </div>
        </div>

        <!-- Calendar View -->
        <div v-else class="px-0 pb-0 mb-6 bg-white rounded-4 shadow-sm overflow-hidden border-0">
          <div class="calendar-container p-0">
            <div class="calendar-header d-flex justify-content-between align-items-center p-4 bg-light border-bottom">
              <div class="d-flex align-items-center">
                <h5 class="mb-0 fw-bold text-dark">{{ currentMonthYear }}</h5>
              </div>
              <div class="d-flex gap-2">
                <button @click="previousMonth" class="btn btn-white border shadow-sm btn-sm px-3">
                  <AppIcon name="bi-chevron-left" class="bi bi-chevron-left" />
                </button>
                <button @click="calendarDate = new Date(); fetchReservations()" class="btn btn-white border shadow-sm btn-sm px-3">
                  {{$t('today')}}
                </button>
                <button @click="nextMonth" class="btn btn-white border shadow-sm btn-sm px-3">
                  <AppIcon name="bi-chevron-right" class="bi bi-chevron-right" />
                </button>
              </div>
            </div>
            
            <div class="calendar-grid">
              <!-- Calendar header -->
              <div class="calendar-week-header bg-white">
                <div v-for="day in weekDays" :key="day" class="calendar-day-header py-3">{{ $t(day.toLowerCase()) || day }}</div>
              </div>
              
              <!-- Calendar days -->
              <div class="calendar-week" v-for="(week, wIndex) in calendarWeeks" :key="wIndex">
                <div 
                  v-for="day in week" 
                  :key="day.date.toISOString()" 
                  class="calendar-day"
                  :class="{
                    'other-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-reservations': day.reservations && day.reservations.length > 0,
                    'selected-day': isSelectedDay(day.date)
                  }"
                  @click="selectDate(day.date)"
                >
                  <div class="day-header d-flex justify-content-between align-items-start">
                    <span class="day-number" :class="{'bg-primary text-white rounded-circle d-flex align-items-center justify-content-center': day.isToday}" :style="day.isToday ? 'width:24px; height:24px;' : ''">
                      {{ day.dayNumber }}
                    </span>
                  </div>
                  <div v-if="day.reservations && day.reservations.length > 0" class="reservation-indicators mt-auto">
                    <div 
                      v-for="reservation in day.reservations.slice(0, 2)" 
                      :key="reservation.id"
                      class="reservation-pill"
                      :class="`status-bg-${reservation.status}`"
                    >
                      <span class="pill-dot"></span>
                      <span class="pill-text">{{ reservation.time }}</span>
                    </div>
                    <div v-if="day.reservations.length > 2" class="more-indicator text-muted small ps-1">
                      +{{ day.reservations.length - 2 }} more
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    <!-- Create Modal -->
    <div class="modal fade" id="create-modal" tabindex="-1" aria-labelledby="create-modal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-dark">{{$t('create_reservation')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body p-4" style="text-align:left">
            <div class="row g-3">
              <div class="col-sm-6">
                <label for="dateInput" class="form-label small fw-bold text-muted text-uppercase">{{$t('date')}}</label>
                <date-picker v-model="reservation.date" type="date" :clearable="true" :editable="false" class="w-100 premium-datepicker"></date-picker>
              </div>
              <div class="col-sm-6">
                <label for="timeInput" class="form-label small fw-bold text-muted text-uppercase">{{$t('time')}}</label>
                <select v-model="reservation.time" class="form-select rounded-3 shadow-none border-light-subtle">
                  <option value="" disabled>{{ $t('select_time') || 'Select time' }}</option>
                  <optgroup v-for="section in timeSections" :key="section.label" :label="section.label">
                    <option v-for="slot in section.slots" :key="slot" :value="slot">{{ slot }}</option>
                  </optgroup>
                </select>
              </div>
              <div class="col-sm-6">
                <label for="clientSearch" class="form-label small fw-bold text-muted text-uppercase">{{$t('client')}}</label>
                <auto-complete  
                  @emitAutoCompleteClient="emitAutoCompleteClient" 
                  :defaultValue="reservation?.client?.name"
                  entity="clients"
                  emitEvent="emitAutoCompleteClient"
                ></auto-complete>
                <div class="related-create-link" style="padding: 5px;font-weight: bold;font-size: 11px;" @click="openCreateClientModal">
                  <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{$t('create_new_client')}}
                </div>
              </div>
              <div class="col-sm-6">
                <label for="statusInput" class="form-label small fw-bold text-muted text-uppercase">{{$t('status')}}</label>
                <select v-model="reservation.status" class="form-select rounded-3 shadow-none border-light-subtle" id="statusInput">
                  <option value="pending">{{$t('pending')}}</option>
                  <option value="confirmed">{{$t('confirmed')}}</option>
                  <option value="cancelled">{{$t('cancelled')}}</option>
                </select>
              </div>
              <div class="col-sm-12">
                <collapsible-notes-field v-model="reservation.notes" id="reservation-notes" rows="2" :placeholder="$t('add_details_or_special_requests')" />
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 p-4">
            <button type="button" class="btn btn-light px-4 rounded-3 fw-bold" @click="closeCreateModal">{{$t('close')}}</button>
            <button type="button" class="btn btn-primary px-4 rounded-3 fw-bold shadow-sm" :disabled="loading" @click="saveRow">
              <AppIcon name="bi-check-circle" class="bi bi-check-circle me-2" />{{$t('create')}}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Create modal -->

    <!-- Edit Modal -->
    <div class="modal fade" id="edit-modal" tabindex="-1" aria-labelledby="edit-modal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-dark">{{$t('edit_reservation')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body p-4" v-if="reservation?.id" style="text-align:left">
            <div class="row g-3">
              <div class="col-sm-6">
                <label for="dateInputEdit" class="form-label small fw-bold text-muted text-uppercase">{{$t('date')}}</label>
                <date-picker v-model="reservation.date" type="date" :clearable="true" :editable="false" class="w-100 premium-datepicker"></date-picker>
              </div>
              <div class="col-sm-6">
                <label for="timeInputEdit" class="form-label small fw-bold text-muted text-uppercase">{{$t('time')}}</label>
                <select v-model="reservation.time" class="form-select rounded-3 shadow-none border-light-subtle">
                  <option value="" disabled>{{ $t('select_time') || 'Select time' }}</option>
                  <optgroup v-for="section in timeSections" :key="section.label" :label="section.label">
                    <option v-for="slot in section.slots" :key="slot" :value="slot">{{ slot }}</option>
                  </optgroup>
                </select>
              </div>
              <div class="col-sm-6">
                <label for="clientSearchEdit" class="form-label small fw-bold text-muted text-uppercase">{{$t('client')}}</label>
                <auto-complete  
                  @emitAutoCompleteClient="emitAutoCompleteClient" 
                  :defaultValue="reservation?.client?.name"
                  entity="clients"
                  emitEvent="emitAutoCompleteClient"
                ></auto-complete>
              </div>
              <div class="col-sm-6">
                <label for="statusInputEdit" class="form-label small fw-bold text-muted text-uppercase">{{$t('status')}}</label>
                <select v-model="reservation.status" class="form-select rounded-3 shadow-none border-light-subtle" id="statusInputEdit">
                  <option value="pending">{{$t('pending')}}</option>
                  <option value="confirmed">{{$t('confirmed')}}</option>
                  <option value="cancelled">{{$t('cancelled')}}</option>
                </select>
              </div>
              <div class="col-sm-12">
                <collapsible-notes-field v-model="reservation.notes" id="reservation-edit-notes" rows="2" :placeholder="$t('add_details_or_special_requests')" />
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 p-4">
            <button type="button" class="btn btn-light px-4 rounded-3 fw-bold" @click="closeEditModal">{{$t('close')}}</button>
            <button type="button" class="btn btn-success px-4 rounded-3 fw-bold shadow-sm" :disabled="loading" @click="updateRow"> 
              <AppIcon name="bi-pencil" class="bi bi-pencil me-2" />{{$t('update')}}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Edit modal -->

    <!-- Client Modal -->
    <add-client 
        v-if="createClientState"  
        :state="createClientState"  
        @createClient="createClient"/>
    <!-- Client Modal -->

    <!-- Day Reservations Modal -->
    <div class="modal fade" id="day-modal" tabindex="-1" aria-labelledby="day-modal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <div class="modal-header border-0 pb-0">
            <div class="d-flex flex-column">
              <h5 class="modal-title fw-bold text-primary">{{ formatSelectedDay() }}</h5>
              <p class="text-muted small mb-0">{{ selectedDayReservations.length }} {{ $t('reservations') }}</p>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body p-4">
            <div v-if="loadingDayView" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('loading') }}</span>
              </div>
            </div>
            <div v-else-if="selectedDayReservations.length === 0" class="text-center py-5">
              <div class="mb-3">
                <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-muted" style="font-size: 3rem;" />
              </div>
              <h5 class="text-muted">{{ $t('no_reservations_for_day') }}</h5>
              <p class="text-muted small">{{ $t('click_button_below_to_create') || 'Click the button below to create a new reservation.' }}</p>
            </div>
            <div v-else class="reservation-list">
              <div 
                v-for="reservation in selectedDayReservations" 
                :key="reservation.id"
                class="reservation-item-card p-3 mb-3 rounded-3 border d-flex align-items-center justify-content-between transition-all"
              >
                <div class="d-flex align-items-center">
                  <div class="time-badge rounded-3 bg-light p-2 me-3 text-center" style="min-width: 70px;">
                    <div class="small fw-bold text-dark">{{ reservation.time }}</div>
                  </div>
                  <div>
                    <div class="fw-bold text-dark mb-1">{{ reservation.client?.name || 'Unknown Client' }}</div>
                    <div class="d-flex align-items-center">
                      <span :class="statusBadgeClass(reservation.status)" class="badge rounded-pill me-2 px-3" style="font-size: 10px;">
                        {{ $t(reservation.status) }}
                      </span>
                      <span v-if="reservation.notes" class="text-muted small text-truncate d-inline-block" style="max-width: 200px;">
                        <AppIcon name="bi-sticky" class="bi bi-sticky me-1" />{{ reservation.notes }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="d-flex gap-2">
                  <button @click="viewReservation(reservation); hideDayModal()" class="btn btn-icon btn-light btn-sm rounded-circle">
                    <AppIcon name="bi-eye" class="bi bi-eye text-info" />
                  </button>
                  <button @click="editReservation(reservation); hideDayModal()" class="btn btn-icon btn-light btn-sm rounded-circle">
                    <AppIcon name="bi-pencil" class="bi bi-pencil text-success" />
                  </button>
                  <button @click="deleteReservation(reservation)" class="btn btn-icon btn-light btn-sm rounded-circle">
                    <AppIcon name="bi-trash" class="bi bi-trash text-danger" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0 p-4">
            <button type="button" class="btn btn-light px-4 rounded-3 fw-bold" data-bs-dismiss="modal">{{$t('close')}}</button>
            <button @click="createNewReservationFromDay" class="btn btn-success px-4 rounded-3 fw-bold shadow-sm">
              <AppIcon name="bi-plus-lg" class="bi bi-plus-lg me-2" />{{$t('create_reservation')}}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- View Reservation Modal -->
    <div class="modal fade" id="view-modal" tabindex="-1" aria-labelledby="view-modal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg rounded-4">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-dark">{{ $t('reservation_details') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body p-4" v-if="reservationView">
            <div class="d-flex align-items-center mb-4 p-3 bg-light rounded-3">
              <div class="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" style="width: 50px; height: 50px; font-size: 20px;">
                <AppIcon name="bi-person" class="bi bi-person" />
              </div>
              <div>
                <h6 class="fw-bold mb-0">{{ reservationView.client?.name || 'Unknown Client' }}</h6>
                <p class="text-muted small mb-0">{{ reservationView.client?.email || $t('no_email') }}</p>
              </div>
            </div>

            <div class="row g-3">
              <div class="col-6">
                <label class="text-muted small fw-bold text-uppercase letter-spacing-1 mb-1 d-block">{{ $t('date') }}</label>
                <div class="d-flex align-items-center text-dark fw-bold">
                  <AppIcon name="bi-calendar3" class="bi bi-calendar3 me-2 text-primary" />
                  {{ formatDate(reservationView.date) }}
                </div>
              </div>
              <div class="col-6">
                <label class="text-muted small fw-bold text-uppercase letter-spacing-1 mb-1 d-block">{{ $t('time') }}</label>
                <div class="d-flex align-items-center text-dark fw-bold">
                  <AppIcon name="bi-clock" class="bi bi-clock me-2 text-primary" />
                  {{ reservationView.time }}
                </div>
              </div>
              <div class="col-12">
                <label class="text-muted small fw-bold text-uppercase letter-spacing-1 mb-1 d-block">{{ $t('status') }}</label>
                <div>
                  <span :class="statusBadgeClass(reservationView.status)" class="badge rounded-pill small px-3">
                    {{ $t(reservationView.status) }}
                  </span>
                </div>
              </div>
              <div class="col-12" v-if="reservationView.notes">
                <label class="text-muted small fw-bold text-uppercase letter-spacing-1 mb-1 d-block">{{ $t('notes') }}</label>
                <div class="p-3 bg-light rounded-3 text-dark border-start border-primary border-4">
                  {{ reservationView.notes }}
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 p-4">
            <button v-if="helper.checkAccess('edit_reservations') != -1" @click="editReservation(reservationView); viewModal.hide()" class="btn btn-success px-4 rounded-3 fw-bold">
              <AppIcon name="bi-pencil" class="bi bi-pencil me-2" />{{ $t('edit') }}
            </button>
            <button type="button" class="btn btn-light px-4 rounded-3 fw-bold" data-bs-dismiss="modal">{{$t('close')}}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Action Bar -->
    <div v-if="isSelectedItem" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded-4 d-flex align-items-center gap-4 animate__animated animate__slideInUp" style="z-index: 1050; border: 1px solid #e2e8f0; min-width: 320px; justify-content: space-between;">
        <div class="d-flex align-items-center">
            <div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style="width: 32px; height: 32px; font-size: 14px;">
              {{ selectedCount }}
            </div>
            <span class="text-dark fw-bold">{{$t('selected')}}</span>
        </div>
        <div class="d-flex gap-2">
            <button v-if="helper.checkAccess('delete_reservations') != -1" class="btn btn-danger btn-sm rounded-3 px-3 d-flex align-items-center" @click="deleteSelected">
                <AppIcon name="bi-trash" class="bi bi-trash me-2" /> {{$t('delete')}}
            </button>
        </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import helper from '../helper';
import AutoComplete from '../components/autocomplete/AutoComplete.vue'
import AddClient from '../components/AddClient.vue'
import sortableList from '../mixins/sortableList'

export default {
  name: 'Reservations',
  persistedFilterState: {
    key: 'reservations',
    fields: ['keyword', 'range', 'per_page', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: { DatePicker, AutoComplete, AddClient },
  data() {
    return {
      reservations: [],
      loading: false,
      keyword: '',
      range: [],
      calendarDate: new Date(),
      count: 0,
      page: 1,
      pagination: [],
      per_page: 25,
      createClientState : null,
      reservation: {
        id: null,
        date: new Date(),
        time: new Date(),
        client_id: '',
        resource_id: '',
        status: 'pending',
        notes: '',
        client_name: '',
        client_email: '',
        custom_email: '',
        send_email: false
      },
      createModal: null,
      editModal: null,
      shortcuts: [
        {
          text: this.$t('today'),
          onClick: () => {
            const end = new Date();
            const start = new Date();
            return [start, end];
          }
        },
        {
          text: this.$t('yesterday'),
          onClick: () => {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24);
            end.setTime(end.getTime() - 3600 * 1000 * 24);
            return [start, end];
          }
        },
        {
          text: this.$t('last_7_days'),
          onClick: () => {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
            return [start, end];
          }
        },
        {
          text: this.$t('last_30_days'),
          onClick: () => {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
            return [start, end];
          }
        },
        {
          text: this.$t('tomorrow'),
          onClick: () => {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() + 3600 * 1000 * 24);
            end.setTime(end.getTime() + 3600 * 1000 * 24);
            return [start, end];
          }
        },
        {
          text: this.$t('this_month'),
          onClick: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(1);
            return [start, end];
          }
        }
      ],
      currentView: 'calendar', // 'table' or 'calendar'
      weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      selectedDay: null,
      selectedDayReservations: [],
      loadingDayView: false,
      showTimePicker: false,
      dayModal: null,
      viewModal: null,
      reservationView: null
    };
  },
  computed: {
    timeSections() {
      const generate = (start, end) => {
        const slots = [];
        for (let h = start; h < end; h++) {
          slots.push(`${String(h).padStart(2,'0')}:00`);
          slots.push(`${String(h).padStart(2,'0')}:30`);
        }
        return slots;
      };
      return [
        { label: this.$t('morning') || 'Morning', icon: 'bi bi-sunrise', slots: generate(6, 12) },
        { label: this.$t('afternoon') || 'Afternoon', icon: 'bi bi-sun', slots: generate(12, 18) },
        { label: this.$t('evening') || 'Evening', icon: 'bi bi-moon', slots: generate(18, 24) }
      ];
    },
    reservationsOnCalendar() {
      const month = this.calendarDate.getMonth();
      const year = this.calendarDate.getFullYear();
      return this.reservations.filter(r => {
        const d = new Date(r.date);
        return d.getMonth() === month && d.getFullYear() === year;
      });
    },
    currentMonthYear() {
      return this.calendarDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
      });
    },
    calendarWeeks() {
      const year = this.calendarDate.getFullYear();
      const month = this.calendarDate.getMonth();
      
      // Get first day of month and last day of month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Get the day of week for first day (0 = Sunday, 1 = Monday, etc.)
      const firstDayOfWeek = firstDay.getDay();
      
      // Calculate how many days from previous month to show
      const daysFromPrevMonth = firstDayOfWeek;
      
      // Calculate total days in current month
      const daysInMonth = lastDay.getDate();
      
      // Calculate how many days from next month to show
      const totalDaysToShow = 42; // 6 weeks * 7 days
      const daysFromNextMonth = totalDaysToShow - daysFromPrevMonth - daysInMonth;
      
      const weeks = [];
      let currentWeek = [];
      
      // Add days from previous month
      for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
        const date = new Date(year, month, -i);
        currentWeek.push({
          date: date,
          dayNumber: date.getDate(),
          isCurrentMonth: false,
          isToday: this.isToday(date),
          reservations: this.getReservationsForCalendarDisplay(date)
        });
      }
      
      // Add days from current month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        currentWeek.push({
          date: date,
          dayNumber: day,
          isCurrentMonth: true,
          isToday: this.isToday(date),
          reservations: this.getReservationsForCalendarDisplay(date)
        });
        
        if (currentWeek.length === 7) {
          weeks.push([...currentWeek]);
          currentWeek = [];
        }
      }
      
      // Add days from next month
      for (let day = 1; day <= daysFromNextMonth; day++) {
        const date = new Date(year, month + 1, day);
        currentWeek.push({
          date: date,
          dayNumber: date.getDate(),
          isCurrentMonth: false,
          isToday: this.isToday(date),
          reservations: this.getReservationsForCalendarDisplay(date)
        });
        
        if (currentWeek.length === 7) {
          weeks.push([...currentWeek]);
          currentWeek = [];
        }
      }
      
      return weeks;
    },
    role() { return this.$store.getters.ROLE },
    business() { return this.$store.getters.BUSINESS },
    helper() { return helper },
    isSelectedItem() {
      return this.reservations.some(r => r.selected);
    },
    selectedCount() {
      return this.reservations.filter(r => r.selected).length;
    },
    areAllSelected() {
      return this.reservations.length > 0 && this.reservations.every(r => r.selected);
    }

  },
  watch: {
    range() {
      this.fetchReservations();
    },
    keyword() {
      this.fetchReservations();
    },
    calendarDate() {
      // Optionally, fetch reservations for the selected month
    },
    per_page() {
      this.fetchReservations(this.page);
    }
  },
  mounted() {
    window.mixpanel.track("reservations");
    // Check permissions
    if(helper.checkAccess('view_reservations') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_reservations') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchReservations();

    // Initialize Bootstrap modals
    // eslint-disable-next-line no-undef
    this.createModal = new bootstrap.Modal(document.getElementById('create-modal'),{backdrop: 'static', keyboard: false})
    
    // eslint-disable-next-line no-undef
    this.editModal = new bootstrap.Modal(document.getElementById('edit-modal'),{backdrop: 'static', keyboard: false})

    // eslint-disable-next-line no-undef
    this.dayModal = new bootstrap.Modal(document.getElementById('day-modal'),{backdrop: 'static', keyboard: false})

    // eslint-disable-next-line no-undef
    this.viewModal = new bootstrap.Modal(document.getElementById('view-modal'),{backdrop: 'static', keyboard: false})
  },
  methods: {
    create() {
      this.clearForm();
      this.showTimePicker = false;
      // eslint-disable-next-line no-undef
      this.createModal.show();
    },
    selectTime(slot) {
      this.reservation.time = slot;
      this.showTimePicker = false;
    },
    openCreateClientModal() {
        this.createClientState = !this.createClientState;
    },
    viewReservation(reservation) {
      this.reservationView = reservation;
      this.viewModal.show();
    },
    editReservation(row) {
      try {
        // Create a deep copy of the row data
        this.reservation = { 
          ...row,
          // Convert date string to Date object if it's a string
          date: row.date ? new Date(row.date) : new Date(),
          // Convert time string to Date object if it's a string
          time: row.time || null,
          // Ensure client_name is set for autocomplete
          client_name: row.client?.name || row.client_name || '',
          client_email: row.client?.email || '',
          send_email: false, // Default to false when editing
          custom_email: ''
        };
        this.showTimePicker = false;
        this.editModal.show();
      } catch (error) {
        alert('Error editing reservation:', error);
        this.reservation = { 
          ...row,
          date: new Date(),
          time: row.time || '',
          client_name: row.client?.name || row.client_name || '',
          client_email: row.client?.email || '',
          send_email: false,
          custom_email: ''
        };
        this.showTimePicker = false;
        this.editModal.show();
      }
    },
    async saveRow() {
      try {
        this.loading = true;
        const reservationData = {
          ...this.reservation,
          email: this.reservation.send_email && this.reservation.custom_email ? this.reservation.custom_email : null
        };
        await helper.customPost(`${process.env.VUE_APP_API}/reservations`, reservationData, {showToast: true});
        this.loading = false;
        this.clearForm();
        this.fetchReservations();
        this.createModal.hide();
      } catch(error) {
        this.loading = false;
      }
    },
    async updateRow() {
      try {
        this.loading = true;
        const reservationData = {
          ...this.reservation,
          email: this.reservation.send_email && this.reservation.custom_email ? this.reservation.custom_email : null
        };
        await helper.customPatch(`${process.env.VUE_APP_API}/reservations/${this.reservation.id}`, reservationData, {showToast: true});
        this.loading = false;
        this.clearForm();
        this.editModal.hide();
        this.fetchReservations();
      } catch(error) {
        this.loading = false;
        alert('Error updating reservation:', error);
      }
    },
    closeEditModal() {
      this.clearForm();
      this.editModal.hide();
    },
    async deleteReservation(row) {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      });
      if (result.isConfirmed) {
        await helper.customDelete(`${process.env.VUE_APP_API}/reservations/${row.id}`);
        this.reservations = helper.withoutDeletedRecords(this.reservations, row.id);
        this.count = Math.max(0, this.count - 1);
      }
    },
    clearForm() {
      this.reservation = {
        id: null,
        date: new Date(),
        time: '',
        client_id: '',
        resource_id: '',
        status: 'pending',
        notes: '',
        client_name: '',
        client_email: '',
        custom_email: '',
        send_email: false
      };
      this.showTimePicker = false;
    },
    async fetchReservations(page) {
      this.loading = true;
      this.page = page ? page : 1;
      this.reservations = this.page == 1 ? [] : this.reservations;
      
      const params = new URLSearchParams({ page: this.page, per_page: this.per_page });
      this.appendSortParams(params);
      
      // Only apply date range filter if we're in table view
      if (this.currentView === 'table' && this.range.length && this.range[0] && this.range[1]) {
        params.append('start', helper.formatDate(this.range[0]));
        params.append('end', helper.formatDate(this.range[1]));
      }
      
      if (this.keyword) {
        params.append('keyword', this.keyword);
      }
      const url = `${process.env.VUE_APP_API}/reservations?${params.toString()}`;
      
      try {
        const response = await helper.customGet(url);
        this.reservations = response.data.data || response.data;
        this.count = response.data.count || response.data.length;
        this.page = response.data.current_page || 1;
        
        // Pagination
        this.pagination = [];
        let MaxPagination = Math.ceil(this.count / this.per_page);
        for(let i = 1; i <= MaxPagination; i++) {
          this.pagination.push(i);
        }
      } catch (e) {
        alert('error')
        this.reservations = [];
        this.count = 0;
      }
      this.loading = false;
    },
    formatDate(date) {
      return helper.formatDate(date);
    },
    statusClass(status) {
      return {
        'badge': true,
        'badge active': status === 'confirmed',
        'badge warning': status === 'pending',
        'badge danger': status === 'cancelled',
        'badge expired': !['confirmed', 'pending', 'cancelled'].includes(status)
      };
    },
    emitAutoCompleteClient(data) {
      this.reservation.client_id = data.result.id;
      this.reservation.client_name = data.result.name;
      // Store client email for email functionality
      this.reservation.client_email = data.result.email;
    },
    closeCreateModal() {
      this.clearForm();
      this.createModal.hide();
    },
    createClient(data) {
        if(data.client) {
            this.reservation.client_id = data.client.id;
            this.reservation.client_name = data.client.name;
        }
        if(data && data.state != undefined) {
            this.createClientState = data.state;
        }
    },
    goToInsights() {
      this.$router.push({ path: '/reservations-insights' });
    },
    toggleView() {
      this.currentView = this.currentView === 'table' ? 'calendar' : 'table';
      this.calendarDate = new Date(); // Reset calendar to current month
      
      // Clear date range when switching to calendar view to fetch all reservations
      if (this.currentView === 'calendar') {
        this.range = [];
      }
      
      this.fetchReservations(); // Re-fetch reservations for the new view
    },
    previousMonth() {
      this.calendarDate.setMonth(this.calendarDate.getMonth() - 1);
      this.fetchReservations();
    },
    nextMonth() {
      this.calendarDate.setMonth(this.calendarDate.getMonth() + 1);
      this.fetchReservations();
    },
    selectDate(date) {
      this.openFullDayView(date);
    },
    createNewReservationFromDay() {
      this.clearForm();
      this.reservation.date = new Date(this.selectedDay);
      this.dayModal.hide();
      this.createModal.show();
    },
    hideDayModal() {
      this.dayModal.hide();
    },
    isToday(date) {
      const today = new Date();
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear();
    },
    isSelectedDay(date) {
      if (!this.selectedDay || !date) return false;
      return this.selectedDay.getDate() === date.getDate() &&
             this.selectedDay.getMonth() === date.getMonth() &&
             this.selectedDay.getFullYear() === date.getFullYear();
    },
    async openFullDayView(date) {
      this.selectedDay = date;
      this.dayModal.show();
      await this.fetchReservationsForDate(date);
    },
    async fetchReservationsForDate(date) {
      try {
        this.loadingDayView = true
        const formattedDate = helper.formatDate(date);
        const url = `${process.env.VUE_APP_API}/reservations?date=${formattedDate}`;
        
        const response = await helper.customGet(url);
        const reservations = response.data.data || response.data || [];
        
        // Use this.$set to ensure Vue reactivity
        this.selectedDayReservations = reservations

      } catch (error) {
        alert('error')
      } finally {
        this.loadingDayView = false
      }
    },
    formatTime(time) {
      return (time);
    },
    formatSelectedDay() {
      if (this.selectedDay) {
        return helper.formatDate(this.selectedDay);
      }
      return '';
    },
    closeFullDayView() {
      this.selectedDay = null;
    },
    getReservationsForCalendarDisplay(date) {
      if (!date || !this.reservations) return [];
      
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
      
      return this.reservations.filter(r => {
        const reservationDate = new Date(r.date);
        reservationDate.setHours(0, 0, 0, 0);
        return reservationDate.getTime() === targetDate.getTime();
      });
    },
    useClientEmail() {
      this.reservation.custom_email = this.reservation.client_email;
    },
    statusBadgeClass(status) {
      return {
        'bg-success bg-opacity-10 text-success': status === 'confirmed',
        'bg-warning bg-opacity-10 text-warning': status === 'pending',
        'bg-danger bg-opacity-10 text-danger': status === 'cancelled',
        'bg-secondary bg-opacity-10 text-secondary': !['confirmed', 'pending', 'cancelled'].includes(status)
      };
    },
    toggleSelectAll() {
      const newValue = !this.areAllSelected;
      this.reservations.forEach(r => {
        this.$set(r, 'selected', newValue);
      });
    },
    changeSelectedItem() {
      // Vue 2 reactivity update
      this.$forceUpdate();
    },
    deleteSelected() {
      const selectedIds = this.reservations.filter(r => r.selected).map(r => r.id);
      if (selectedIds.length === 0) return;
      
      this.$swal.fire({
        title: this.$t('are_you_sure'),
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete_it')
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.loading = true;
          try {
            await helper.customPost('/reservations/bulk-delete', { ids: selectedIds });
            this.reservations = helper.withoutDeletedRecords(this.reservations, selectedIds);
            this.count = Math.max(0, this.count - selectedIds.length);
          } catch (err) {
            alert(err)
          } finally {
            this.loading = false;
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.badge {
  padding: 0.25rem 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.table-borderless>:not(caption)>*>*{
    padding: 12px 24px !important;
}

/* Calendar Styles */
.calendar-container {
  background: #fff;
}

.calendar-header {
  border-bottom: 1px solid #f1f5f9;
}

.calendar-grid {
  display: flex;
  flex-direction: column;
  background-color: #f1f5f9;
}

.calendar-week-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #f1f5f9;
}

.calendar-day-header {
  text-align: center;
  font-weight: 700;
  color: #64748b;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.calendar-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #fff;
}

.calendar-day {
  min-height: 120px;
  padding: 1rem;
  border-right: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;
  display: flex;
  flex-direction: column;
}

.calendar-day:last-child {
  border-right: none;
}

.calendar-day:hover {
  background-color: #f8fafc;
  z-index: 1;
  box-shadow: inset 0 0 0 2px #303030;
}

.calendar-day.other-month {
  background-color: #f8fafc;
  color: #cbd5e1;
}

.calendar-day.today {
  background-color: #f0f7ff;
}

.calendar-day.today .day-number {
  background-color: #303030;
  color: white;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.calendar-day.selected-day {
  background-color: #f0f7ff;
  box-shadow: inset 0 0 0 2px #303030;
}

.day-number {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.reservation-indicators {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reservation-pill {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.pill-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  margin-right: 6px;
  background-color: currentColor;
}

.status-bg-pending {
  background-color: #FEF3C7;
  color: #92400E;
}

.status-bg-confirmed {
  background-color: #DCFCE7;
  color: #15803d;
}

.status-bg-cancelled {
  background-color: #FEE2E2;
  color: #b91c1c;
}

/* Modal Styles */
.reservation-item-card {
  border: 1px solid #f1f5f9;
  background-color: #fff;
  transition: all 0.2s ease;
}

.reservation-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  border-color: #303030;
}

.time-badge {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.transition-all {
  transition: all 0.2s ease-in-out;
}

.rounded-4 {
  border-radius: 1rem !important;
}

.btn-primary {
  background: #004aad;
  border: none;
}

.btn-primary:hover {
  background: #1a1a1a;
}

.btn-success {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  border: none;
}

.btn-success:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5;
  border-radius: 0.4rem;
}

.table-active {
  background-color: rgba(59, 130, 246, 0.05) !important;
}

.animate__slideInUp {
  animation-name: slideInUp;
  animation-duration: 0.3s;
}

@keyframes slideInUp {
  from { transform: translate(-50%, 100%); opacity: 0; }
  to { transform: translate(-50%, 0); opacity: 1; }
}

/* DatePicker override to match design */
.premium-datepicker >>> .mx-input {
  border-radius: 0.5rem;
  border-color: #e2e8f0;
  box-shadow: none;
}

.pagination .page-link {
  color: #64748b;
  background-color: #fff;
}

.pagination .active .page-link {
  background-color: #303030;
  color: #fff;
}
</style>
